// CORE-CARDS (flag `escalation_cards`): an approval's escalation card, wherever the approval shows
// (the inbox row, the chat card, the approval page): the situation, the options with their
// consequence, the recommended one, the approver and the deadline, and the rulings that apply
// (CORE-PRECEDENTS: each with its id, date and whether it is the same case or a similar one).
// Pending: one button per option (`POST approvals/{id}/choose`) and the "Apply to similar cases"
// tick (the ruling's `standing`). Decided: the chosen option is marked. An approval without a
// card never reaches this component; it renders as before.
import { Badge, Button, cn } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { ApprovalRow } from "../../api/types";
import { chatKeys } from "../chat/chat-queries";
import { problemText } from "../de/de-status";
import { formatDateTime } from "../format";
import { seatQuery } from "../queries";
import { agentKeys, invalidateDecided } from "./agent-queries";

export type ApprovalCard = NonNullable<ApprovalRow["card"]>;

type Size = "xs" | "sm" | "default" | "lg";

/** The label of the option a decided card chose (null: none chosen). */
export function chosenLabel(a: Pick<ApprovalRow, "card" | "chosen_option">): string | null {
  if (!a.card || !a.chosen_option) return null;
  return a.card.options.find((o) => o.key === a.chosen_option)?.label ?? a.chosen_option;
}

export function EscalationCard({
  api,
  co,
  approval: a,
  size = "sm",
  decide = true,
  compact = false,
  onDecided,
}: {
  api: Api;
  co: string;
  approval: ApprovalRow & { card: ApprovalCard };
  size?: Size;
  /** Offer the option buttons while pending (false: read only). */
  decide?: boolean;
  /** The inbox row: no precedents block, consequences clamped. */
  compact?: boolean;
  onDecided?: (decided: ApprovalRow) => void;
}) {
  const card = a.card;
  const qc = useQueryClient();
  const approver = useQuery({
    ...seatQuery(api, co, card.approver_seat_id),
    retry: false,
    staleTime: 60_000,
  });
  // CORE-PRECEDENTS: "Apply to similar cases" — sent only once ticked or unticked (unset: the
  // company's default for rejections); ignored by the server while `precedents` is off
  const [standing, setStanding] = useState<boolean | null>(null);
  const choose = useMutation({
    mutationFn: (option: string) =>
      api.client.approvals.choose({
        params: { co, id: a.id },
        body: { option, ...(standing !== null ? { standing } : {}) },
      }),
    onSuccess: (d) => {
      qc.setQueryData(agentKeys.approval(co, d.id), d);
      qc.setQueryData(chatKeys.approval(co, d.id), d);
      onDecided?.(d);
    },
    onSettled: () => invalidateDecided(qc, co),
  });
  const pending = a.state === "pending";
  const chosen = a.chosen_option;
  const meta = [
    `Approver: ${approver.data?.title ?? "approver seat"}`,
    card.deadline ? `Due ${formatDateTime(card.deadline)}` : null,
  ].filter(Boolean);
  return (
    <section
      data-slot="escalation-card"
      data-approval={a.id}
      data-chosen={chosen ?? undefined}
      aria-label="Decision card"
      className="flex flex-col gap-2"
    >
      <p className={cn("m-0 text-sm text-foreground", compact && "line-clamp-3")}>
        {card.situation}
      </p>
      <ul className="m-0 flex list-none flex-col gap-1.5 p-0" aria-label="Options">
        {card.options.map((o) => {
          const recommended = o.key === card.recommended;
          const isChosen = o.key === chosen;
          return (
            <li
              key={o.key}
              data-slot="escalation-option"
              data-option={o.key}
              className={cn(
                "flex flex-wrap items-start gap-2 rounded-md border px-2.5 py-1.5",
                isChosen ? "border-primary bg-primary-soft" : "border-border bg-card",
              )}
            >
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-foreground">
                  {o.label}
                  {recommended ? <Badge variant="success">Recommended</Badge> : null}
                  {isChosen ? <Badge variant="accent">Chosen</Badge> : null}
                </span>
                <span className={cn("text-xs text-muted-foreground", compact && "line-clamp-2")}>
                  {o.consequence}
                </span>
              </span>
              {pending && decide ? (
                <Button
                  size={size}
                  variant={recommended ? "default" : "outline"}
                  disabled={choose.isPending}
                  onClick={() => choose.mutate(o.key)}
                  aria-label={`Choose: ${o.label}`}
                >
                  {choose.isPending && choose.variables === o.key ? "Choosing…" : "Choose"}
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
      {!compact && card.precedents.length > 0 ? (
        <div data-slot="escalation-precedents" className="flex flex-col gap-1">
          <span className="text-xs font-medium text-foreground">Rulings that apply</span>
          <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
            {card.precedents.map((p) => (
              <li
                key={p.record_id}
                data-slot="escalation-precedent"
                data-ruling={p.record_id}
                data-match={p.match}
                className="text-xs text-muted-foreground"
              >
                <span className="font-medium text-foreground">
                  {p.match === "structural" ? "Same case" : "Similar"}
                </span>
                {` · ${p.summary}`}
                {p.standing ? " · standing" : ""}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {pending && decide && !compact ? (
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="checkbox"
            data-slot="escalation-standing"
            checked={standing === true}
            onChange={(e) => setStanding(e.currentTarget.checked)}
          />
          Apply to similar cases
        </label>
      ) : null}
      <p className="m-0 text-xs text-muted-foreground" data-slot="escalation-meta">
        {meta.join(" · ")}
      </p>
      {choose.isError ? (
        <span role="alert" className="text-sm text-destructive">
          {problemText(choose.error, "The option could not be chosen.")}
        </span>
      ) : null}
    </section>
  );
}

/** Narrows an approval to one with a card. */
export const hasCard = (a: ApprovalRow): a is ApprovalRow & { card: ApprovalCard } =>
  a.card != null;
